import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { IoAddOutline } from "react-icons/io5";
import { HiOutlineSearch } from "react-icons/hi";
import { BsFillPersonLinesFill } from "react-icons/bs";
import { BiExport } from "react-icons/bi";
import { RiDeleteBin6Line } from "react-icons/ri";
import Button from "../Utilities/Button";
import PaginationNew from "../Utilities/PaginationNew";
import Modal from "../Utilities/Modal";
import Alert from "../Utilities/Alert";
import Toast from "../Utilities/Toast";
import RightPanelLayout from "../Layout/RightPanelLayout";
import EventStatusBadge from "./EventStatusBadge";
import RegistrationCountCell from "./RegistrationCountCell";
import styles from "./style/EventList.module.scss";
import {
  deleteActivity,
  exportActivityRegistrationsCsv,
  fetchActivitiesList,
  fetchActivityRegistrations,
  publishActivity,
  unpublishActivity,
} from "./activitiesApi";
import {
  getPublishMissingFields,
  getRegistrationCountStats,
  listRowToPublishCheckForm,
  mapRegistrationApiRowToModal,
  normalizeActivityListRow,
  normalizeRegistrationListResponse,
} from "./activityTransforms";
import { computeActivityStatus } from "./activityStatus";
import { MdClear, MdDriveFileRenameOutline } from "react-icons/md";
import Switcher from "../Utilities/AlertSwitch/Switcher";

const LIMIT = 10;
const REGISTRATION_MODAL_LIMIT = 10;
const EMPTY_DISPLAY = "未設定";

const mutedUnset = (value) =>
  value === EMPTY_DISPLAY ? (
    <span className={styles.placeholderText}>{EMPTY_DISPLAY}</span>
  ) : (
    value
  );

const renderDateTimeBlock = (dateStr, timeStr, blockClassName) => {
  const bothUnset =
    dateStr === EMPTY_DISPLAY && timeStr === EMPTY_DISPLAY;
  if (bothUnset) {
    return (
      <div className={blockClassName}>
        <div className="whitespace-nowrap">{mutedUnset(EMPTY_DISPLAY)}</div>
      </div>
    );
  }
  return (
    <div className={blockClassName}>
      <div className="whitespace-nowrap">{mutedUnset(dateStr)}</div>
      <div>{mutedUnset(timeStr)}</div>
    </div>
  );
};

const toValidDate = (value) => {
  if (value == null) {
    return null;
  }
  if (typeof value === "string" && value.trim() === "") {
    return null;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  return d;
};

const formatSingleDayEventTime = (startAt, endAt) => {
  const startDate = toValidDate(startAt);
  const endDate = toValidDate(endAt);
  if (!startDate || !endDate) {
    return { date: EMPTY_DISPLAY, timeRange: EMPTY_DISPLAY };
  }
  return {
    date: format(startDate, "yyyy-MM-dd"),
    timeRange: `${format(startDate, "HH:mm")}-${format(endDate, "HH:mm")}`,
  };
};

const formatPublishWindow = (publishedAt, unpublishedAt) => {
  const start = toValidDate(publishedAt);
  const end = toValidDate(unpublishedAt);
  return {
    startDate: start ? format(start, "yyyy-MM-dd") : EMPTY_DISPLAY,
    startTime: start ? format(start, "HH:mm") : EMPTY_DISPLAY,
    endDate: end ? format(end, "yyyy-MM-dd") : EMPTY_DISPLAY,
    endTime: end ? format(end, "HH:mm") : EMPTY_DISPLAY,
  };
};

const formatRegistrationDateTime = (value) => {
  if (!value) {
    return { date: EMPTY_DISPLAY, time: EMPTY_DISPLAY };
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return { date: EMPTY_DISPLAY, time: EMPTY_DISPLAY };
  }
  return { date: format(d, "yyyy-MM-dd"), time: format(d, "HH:mm") };
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const sanitizeFileName = (value) =>
  String(value || "")
    .trim()
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, "_");

const EventList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchApplied, setSearchApplied] = useState("");
  const [registrationSearchKeyword, setRegistrationSearchKeyword] =
    useState("");
  const [openedRegistrationEventId, setOpenedRegistrationEventId] =
    useState(null);
  const [registrationModalTitle, setRegistrationModalTitle] = useState("");
  const [registrationModalPage, setRegistrationModalPage] = useState(1);
  const [registrationRows, setRegistrationRows] = useState([]);
  const [registrationTotal, setRegistrationTotal] = useState(0);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [registrationExporting, setRegistrationExporting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [publishBusyId, setPublishBusyId] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [toastState, setToastState] = useState({
    show: false,
    title: "",
    subtitle: "",
    error: false,
  });

  const loadEvents = useCallback(async (opts = {}) => {
    const silent = Boolean(opts.silent);
    if (!silent) {
      setListLoading(true);
    }
    try {
      const data = await fetchActivitiesList({
        search: searchApplied,
        page,
        limit: LIMIT,
      });
      const rows = data?.rows ?? [];
      setEvents(rows.map(normalizeActivityListRow));
      setTotal(Number(data?.total) || 0);
    } catch {
      if (!silent) {
        setEvents([]);
        setTotal(0);
      }
    } finally {
      if (!silent) {
        setListLoading(false);
      }
    }
  }, [page, searchApplied]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const applyListSearch = () => {
    setSearchApplied(searchInput.trim());
    setPage(1);
  };

  const registrationModalTotalPages = useMemo(
    () =>
      Math.max(
        1,
        Math.ceil(registrationTotal / REGISTRATION_MODAL_LIMIT),
      ),
    [registrationTotal],
  );

  useEffect(() => {
    if (!openedRegistrationEventId) {
      setRegistrationRows([]);
      setRegistrationTotal(0);
      return;
    }
    let cancelled = false;
    setRegistrationLoading(true);
    fetchActivityRegistrations(openedRegistrationEventId, {
      search: registrationSearchKeyword.trim(),
      page: registrationModalPage,
      limit: REGISTRATION_MODAL_LIMIT,
    })
      .then((raw) => {
        if (cancelled) {
          return;
        }
        const { rows, total: t } =
          normalizeRegistrationListResponse(raw);
        setRegistrationRows(rows.map(mapRegistrationApiRowToModal));
        setRegistrationTotal(Number(t) || 0);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setRegistrationRows([]);
        setRegistrationTotal(0);
      })
      .finally(() => {
        if (!cancelled) {
          setRegistrationLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    openedRegistrationEventId,
    registrationModalPage,
    registrationSearchKeyword,
  ]);

  useEffect(() => {
    setRegistrationModalPage(1);
  }, [openedRegistrationEventId, registrationSearchKeyword]);

  const onPageChange = (selectedObj) => {
    setPage(selectedObj.selected + 1);
  };

  const onRegistrationModalPageChange = (selectedObj) => {
    setRegistrationModalPage(selectedObj.selected + 1);
  };

  const handlePublishToggle = async (eventItem, nextChecked) => {
    if (nextChecked) {
      const missing = getPublishMissingFields(
        listRowToPublishCheckForm(eventItem),
      );
      if (missing.length > 0) {
        setToastState({
          show: true,
          title: "尚有未填寫欄位",
          subtitle: "",
          error: true,
        });
        window.setTimeout(() => {
          setToastState((s) => ({ ...s, show: false }));
        }, 3200);
        return;
      }
    }
    setPublishBusyId(eventItem.id);
    try {
      const raw = nextChecked
        ? await publishActivity(eventItem.id)
        : await unpublishActivity(eventItem.id);
      if (nextChecked) {
        setToastState({
          show: true,
          title: `已成功上架活動「${eventItem.name}」。`,
          subtitle: "",
          error: false,
        });
        window.setTimeout(() => {
          setToastState((s) => ({ ...s, show: false }));
        }, 3200);
      }
      if (raw != null && typeof raw === "object") {
        const normalized = normalizeActivityListRow(raw);
        setEvents((prev) =>
          prev.map((e) => {
            if (e.id !== eventItem.id) {
              return e;
            }
            return {
              ...e,
              ...normalized,
              name: normalized.name || e.name,
            };
          }),
        );
      } else {
        await loadEvents({ silent: true });
      }
    } catch {
      await loadEvents({ silent: true });
    } finally {
      setPublishBusyId(null);
    }
  };

  const handleDeleteEvent = async () => {
    if (!deleteTarget) {
      return;
    }
    setDeleteSubmitting(true);
    try {
      await deleteActivity(deleteTarget.id);
      setDeleteTarget(null);
      if (page === 1) {
        await loadEvents();
      } else {
        setPage(1);
      }
    } catch {
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleExportRegistrationCsv = async () => {
    if (!openedRegistrationEventId || registrationExporting) {
      return;
    }
    setRegistrationExporting(true);
    try {
      const res = await exportActivityRegistrationsCsv(
        openedRegistrationEventId,
        { search: registrationSearchKeyword.trim() },
      );
      const disposition = res?.headers?.["content-disposition"] ?? "";
      const filenameMatch = disposition.match(/filename="?([^"]+)"?/i);
      const safeTitle = sanitizeFileName(
        registrationModalTitle || `activity_${openedRegistrationEventId}`,
      );
      const fallbackName = `${safeTitle}_報名列表_${format(
        new Date(),
        "yyyyMMdd_HHmmss",
      )}.csv`;
      downloadBlob(res.data, (filenameMatch && filenameMatch[1]) || fallbackName);
      setToastState({
        show: true,
        title: "已匯出報名 CSV",
        subtitle: "",
        error: false,
      });
      window.setTimeout(() => {
        setToastState((s) => ({ ...s, show: false }));
      }, 2200);
    } catch {
      setToastState({
        show: true,
        title: "匯出失敗請重試",
        subtitle: "",
        error: true,
      });
      window.setTimeout(() => {
        setToastState((s) => ({ ...s, show: false }));
      }, 3200);
    } finally {
      setRegistrationExporting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <RightPanelLayout title="活動管理">
      <div className={styles.listToolbar}>
        <div className={styles.listToolbarTitle}>活動列表</div>
        <div className={styles.toolbarActions}>
          <div className={styles.searchBar}>
            <button
              type="button"
              className={styles.searchBarIconBtn}
              onClick={applyListSearch}
              aria-label="搜尋"
            >
              <HiOutlineSearch className={styles.searchBarIcon} />
            </button>
            <input
              type="search"
              className={styles.searchBarInput}
              value={searchInput}
              placeholder="搜尋活動名稱"
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applyListSearch();
                }
              }}
              aria-label="搜尋活動名稱"
            />
            {searchInput ? (
              <button
                type="button"
                className={styles.searchBarClear}
                onClick={() => {
                  setSearchInput("");
                  setSearchApplied("");
                  setPage(1);
                }}
                aria-label="清除搜尋"
              >
                <MdClear className={styles.searchBarClearIcon} />
              </button>
            ) : null}
          </div>
          <Button type="primary" title="搜尋" onClick={applyListSearch} />
          <Button
            type="primary"
            title="新增"
            icon={<IoAddOutline fontSize={20} />}
            onClick={() => navigate("/dashboard/tdp/events/new")}
          />
        </div>
      </div>

      {!listLoading && total > 0 && (
        <div className={styles.tablePaginationTop}>
          <PaginationNew
            onPageChange={onPageChange}
            pageCount={page}
            limit={LIMIT}
            totalPages={totalPages}
            totalData={total}
          />
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>活動名稱</th>
              <th>狀態</th>
              <th>上架狀態</th>
              <th>活動時間</th>
              <th>上架時間</th>
              <th>報名人數</th>
              <th>報名列表</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {listLoading ? (
              <tr>
                <td colSpan={8} className={styles.tableLoadingCell}>
                  載入中…
                </td>
              </tr>
            ) : null}
            {!listLoading && events.length === 0 ? (
              <tr>
                <td colSpan={8} className={styles.tableEmptyCell}>
                  尚無活動資料
                </td>
              </tr>
            ) : null}
            {!listLoading
              ? events.map((eventItem) => {
                  const registrationStats = getRegistrationCountStats(eventItem);
                  const statusConfig = computeActivityStatus({
                    isPublished: eventItem.isPublished,
                    activityStartAt: eventItem.eventStartAt,
                    publishedAt: eventItem.publishedAt,
                    registered: registrationStats.registered,
                    capacity: registrationStats.capacity,
                  });
                  return (
                    <tr key={eventItem.id}>
                    <td>{eventItem.name}</td>
                    <td>
                      <EventStatusBadge
                        theme={statusConfig.theme}
                        text={statusConfig.text}
                      />
                    </td>
                    <td>
                      <div className={styles.publishSwitchWrap}>
                        <Switcher
                          isChecked={eventItem.isPublished}
                          disabled={publishBusyId === eventItem.id}
                          onChange={(e) => {
                            handlePublishToggle(eventItem, e.target.checked);
                          }}
                          color={{ on: "#5856D6", off: "#5856D64D" }}
                          width={33}
                          height={20}
                        />
                      </div>
                    </td>
                    <td>
                      {(() => {
                        const eventTime = formatSingleDayEventTime(
                          eventItem.eventStartAt,
                          eventItem.eventEndAt,
                        );
                        const eventBothUnset =
                          eventTime.date === EMPTY_DISPLAY &&
                          eventTime.timeRange === EMPTY_DISPLAY;
                        return (
                          <div className={styles.eventTimeCell}>
                            {eventBothUnset ? (
                              <div className="whitespace-nowrap">
                                {mutedUnset(EMPTY_DISPLAY)}
                              </div>
                            ) : (
                              <>
                                <div className="whitespace-nowrap">
                                  {mutedUnset(eventTime.date)}
                                </div>
                                <div className="whitespace-nowrap">
                                  {mutedUnset(eventTime.timeRange)}
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      {(() => {
                        const publishWindow = formatPublishWindow(
                          eventItem.publishedAt,
                          eventItem.unpublishedAt,
                        );
                        return (
                          <div className={styles.registrationTimeCell}>
                            {renderDateTimeBlock(
                              publishWindow.startDate,
                              publishWindow.startTime,
                              styles.registrationTimeBlock,
                            )}
                            <span aria-hidden="true">-</span>
                            {renderDateTimeBlock(
                              publishWindow.endDate,
                              publishWindow.endTime,
                              styles.registrationTimeBlock,
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      <RegistrationCountCell
                        registered={registrationStats.registered}
                        capacity={registrationStats.capacity}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.linkButton}
                        onClick={() => {
                          setOpenedRegistrationEventId(eventItem.id);
                          setRegistrationModalTitle(eventItem.name);
                          setRegistrationSearchKeyword("");
                          setRegistrationModalPage(1);
                        }}
                        aria-label="查看報名"
                      >
                        <BsFillPersonLinesFill />
                      </button>
                    </td>
                    <td>
                      <div className={styles.actions}>
                        <button
                          type="button"
                          className={styles.editButton}
                          onClick={() =>
                            navigate(
                              `/dashboard/tdp/events/${eventItem.id}/edit`,
                            )
                          }
                          aria-label="編輯活動"
                        >
                          <MdDriveFileRenameOutline />
                        </button>
                        <button
                          type="button"
                          className={styles.deleteButton}
                          onClick={() =>
                            setDeleteTarget({
                              id: eventItem.id,
                              name: eventItem.name,
                            })
                          }
                          aria-label="刪除活動"
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })
              : null}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={Boolean(openedRegistrationEventId)}
        size="xl"
        width="min(1218px, calc(100vw - 32px))"
      >
        <div className={styles.registrationModalRoot}>
          <header className={styles.registrationModalTop}>
            <h2 className={styles.registrationModalTitle}>報名列表</h2>
            <button
              type="button"
              className={styles.registrationModalExportBtn}
              disabled={
                registrationExporting ||
                registrationLoading ||
                registrationTotal === 0 ||
                !openedRegistrationEventId
              }
              onClick={handleExportRegistrationCsv}
            >
              <BiExport size={16} aria-hidden />
              {registrationExporting ? "匯出中…" : "匯出"}
            </button>
          </header>

          <div className={styles.registrationModalBody}>
            <div className={styles.registrationModalControls}>
              {!registrationLoading && registrationTotal > 0 ? (
                <PaginationNew
                  wrapperClassName={styles.registrationModalPaginationInner}
                  onPageChange={onRegistrationModalPageChange}
                  pageCount={registrationModalPage}
                  limit={REGISTRATION_MODAL_LIMIT}
                  totalPages={registrationModalTotalPages}
                  totalData={registrationTotal}
                />
              ) : null}
            </div>

            {registrationLoading ? (
              <div className={styles.registrationModalEmpty}>載入中…</div>
            ) : registrationRows.length > 0 ? (
              <div className={styles.modalTableWrap}>
                <table className={styles.modalTable}>
                  <thead>
                    <tr>
                      <th colSpan={2}>報名時間</th>
                      <th>姓名</th>
                      <th>電話</th>
                      <th>E-mail</th>
                      <th>公司</th>
                      <th>職位</th>
                      <th>報名活動</th>
                      <th>備註</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrationRows.map((registration) => {
                      const { date, time } = formatRegistrationDateTime(
                        registration.registeredAt,
                      );
                      return (
                        <tr key={registration.id}>
                          <td className={styles.modalTableMuted}>
                            {mutedUnset(date)}
                          </td>
                          <td className={styles.modalTableMuted}>
                            {mutedUnset(time)}
                          </td>
                          <td>{registration.name}</td>
                          <td className={styles.modalTableStrongLatin}>
                            {registration.phone}
                          </td>
                          <td className={styles.modalTableStrongLatin}>
                            {registration.email}
                          </td>
                          <td>{mutedUnset(registration.companyName)}</td>
                          <td>{mutedUnset(registration.jobTitle)}</td>
                          <td className={styles.modalCellEventName}>
                            {mutedUnset(
                              registrationModalTitle ?? EMPTY_DISPLAY,
                            )}
                          </td>
                          <td>
                            {(registration.notes &&
                              registration.notes.trim()) ||
                              ""}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className={styles.registrationModalEmpty}>
                目前沒有符合條件的報名資料。
              </div>
            )}
          </div>

          <footer className={styles.registrationModalFooter}>
            <button
              type="button"
              className={styles.registrationModalCloseBtn}
              onClick={() => setOpenedRegistrationEventId(null)}
            >
              關閉
            </button>
          </footer>
        </div>
      </Modal>

      <Alert
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleteSubmitting && setDeleteTarget(null)}
        onConfirm={handleDeleteEvent}
        leftButtonText="取消"
        rightButtonText={deleteSubmitting ? "刪除中…" : "刪除"}
        mainTitle="確認刪除活動？"
        subTitle={deleteTarget ? `「${deleteTarget.name}」將被刪除。` : ""}
        isWarning
      />

      <Toast
        show={toastState.show}
        title={toastState.title}
        subtitle={toastState.subtitle}
        error={toastState.error}
      />
    </RightPanelLayout>
  );
};

export default EventList;
